// src/components/RouteDetailModal.js
import React, { useState } from 'react';
import { openDispute, closeDispute } from '../services/routeClosuresService';
import { authService } from '../services/authService';

const toDate = (d) => d?.toDate ? d.toDate() : d instanceof Date ? d : d ? new Date(d) : null;

const fmtDateTime = (d) => {
  const dd = toDate(d);
  if (!dd) return '';
  return dd.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })
    + ' · ' + dd.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
};

const RouteDetailModal = ({ closure, onClose }) => {
  const [note, setNote]       = useState('');
  const [opening, setOpening] = useState(false);
  const [closing, setClosing] = useState(false);
  const [err, setErr]         = useState('');

  const dateObj   = toDate(closure.date);
  const timeObj   = toDate(closure.createdAt || closure.date);
  const assigned  = Number(closure.assigned)  || 0;
  const completed = Number(closure.completed) || 0;
  const completion = assigned > 0 ? Math.round((completed / assigned) * 100) : 0;
  // `cash` es un booleano (¿hubo algo de efectivo?), no el importe — usarlo
  // aquí hacía que Number(true) diera "1€" en vez del importe real guardado
  // en `efectivo` (ver manageRouteClosure.js: cash = efectivo > 0).
  const efectivo   = closure.efectivo;

  const barColor   = completion >= 100 ? 'bg-green-500' : completion >= 80 ? 'bg-amber-500' : 'bg-red-500';
  const labelColor = completion >= 100 ? 'text-green-700' : completion >= 80 ? 'text-amber-700' : 'text-red-700';

  const dispute = closure.disputeStatus;

  const handleOpenDispute = async () => {
    if (!note.trim()) return;
    setOpening(true); setErr('');
    try {
      await openDispute(closure.id, note.trim());
      // Notificar al técnico por push (sin bloquear si falla)
      fetch('/.netlify/functions/sendDisputeNotification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({
          employeeId:    closure.employeeId,
          technicianName: closure.employeeName || '',
          disputeNote:   note.trim(),
        }),
      }).catch(() => {});
      setNote('');
    } catch (e) {
      setErr('Error al abrir la disputa: ' + e.message);
    } finally {
      setOpening(false);
    }
  };

  const handleCloseDispute = async () => {
    setClosing(true); setErr('');
    try {
      await closeDispute(closure.id);
    } catch (e) {
      setErr('Error al cerrar la disputa: ' + e.message);
    } finally {
      setClosing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className={`p-5 rounded-t-2xl flex justify-between items-start ${
          dispute === 'open' ? 'bg-gradient-to-r from-orange-500 to-orange-600'
          : dispute === 'closed' ? 'bg-gradient-to-r from-gray-500 to-gray-600'
          : 'bg-gradient-to-r from-red-600 to-red-700'
        }`}>
          <div>
            <h2 className="text-xl font-bold text-white">
              {closure.employeeName || `Técnico ${closure.employeeId}`}
            </h2>
            <p className="text-white/75 text-sm mt-0.5">
              {dateObj ? dateObj.toLocaleDateString('es-ES', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }) : 'Sin fecha'}
            </p>
            {dispute === 'open' && (
              <span className="inline-block mt-1.5 px-2 py-0.5 bg-white/20 text-white text-xs font-bold rounded-full">
                ⚠ Disputa abierta
              </span>
            )}
            {dispute === 'closed' && (
              <span className="inline-block mt-1.5 px-2 py-0.5 bg-white/20 text-white text-xs font-bold rounded-full">
                ✓ Disputa resuelta
              </span>
            )}
          </div>
          <button onClick={onClose} className="text-white hover:bg-white/20 rounded-full p-1.5 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-5 space-y-4">

          {/* Hora de cierre */}
          {timeObj && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Ruta cerrada a las{' '}
              <strong className="text-gray-800">
                {timeObj.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
              </strong>
            </div>
          )}

          {/* Barra de progreso */}
          <div>
            <div className="flex justify-between items-center mb-1.5">
              <span className="text-sm font-semibold text-gray-700">Órdenes de trabajo</span>
              <span className={`text-sm font-bold ${labelColor}`}>{completion}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div className={`h-3 rounded-full transition-all ${barColor}`} style={{ width: `${Math.min(completion, 100)}%` }} />
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>{completed} completadas</span>
              <span>{assigned} asignadas</span>
            </div>
          </div>

          {/* Efectivo */}
          {efectivo !== undefined && efectivo !== null && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center justify-between">
              <span className="text-sm font-semibold text-green-800">Efectivo cobrado</span>
              <span className="text-lg font-bold text-green-700">€{Number(efectivo).toFixed(2)}</span>
            </div>
          )}

          {/* Avisos de facturación/material detectados al cerrar ruta */}
          {Array.isArray(closure.warnings) && closure.warnings.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
              <p className="text-sm font-bold text-amber-800 mb-2">
                ⚠ {closure.warnings.length} aviso{closure.warnings.length !== 1 ? 's' : ''} pendiente{closure.warnings.length !== 1 ? 's' : ''} de revisar
              </p>
              <div className="space-y-1.5">
                {closure.warnings.map((w, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs">
                    <span className="font-semibold text-amber-700 flex-shrink-0">{w.ref}</span>
                    <span className="text-amber-700">{w.msg}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Sistema de disputas ── */}
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-200 flex items-center gap-2">
              <span className="text-sm font-bold text-gray-700">Dir. de Operaciones</span>
              {!dispute && <span className="text-xs text-gray-400">— Observaciones sobre este cierre</span>}
            </div>

            <div className="p-4 space-y-3">

              {/* Sin disputa — formulario para abrir */}
              {!dispute && (
                <>
                  {/* Nota interna legacy */}
                  {closure.notes && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-600 whitespace-pre-wrap">
                      {closure.notes}
                    </div>
                  )}
                  <textarea
                    value={note}
                    onChange={e => { setNote(e.target.value); setErr(''); }}
                    rows={3}
                    placeholder="Describe el motivo de la disputa..."
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm resize-none"
                  />
                  <div className="flex justify-end">
                    <button
                      onClick={handleOpenDispute}
                      disabled={opening || !note.trim()}
                      className="px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-40 text-white rounded-lg text-sm font-semibold transition-colors flex items-center gap-2"
                    >
                      {opening ? 'Abriendo...' : '⚠ Abrir disputa'}
                    </button>
                  </div>
                </>
              )}

              {/* Disputa abierta o cerrada — hilo */}
              {(dispute === 'open' || dispute === 'closed') && (
                <div className="space-y-3">

                  {/* Mensaje del director */}
                  <div className={`rounded-lg p-3 ${dispute === 'closed' ? 'bg-gray-50 border border-gray-200' : 'bg-orange-50 border border-orange-200'}`}>
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <span className="text-xs font-bold text-orange-700">Dir. de Operaciones</span>
                      <span className="text-xs text-gray-400">· {fmtDateTime(closure.disputeNoteAt)}</span>
                    </div>
                    <p className="text-sm text-gray-800 whitespace-pre-wrap">{closure.disputeNote}</p>
                  </div>

                  {/* Respuesta del técnico */}
                  {closure.disputeReply ? (
                    <div className={`rounded-lg p-3 ${dispute === 'closed' ? 'bg-gray-50 border border-gray-200' : 'bg-blue-50 border border-blue-200'}`}>
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <span className="text-xs font-bold text-blue-700">{closure.employeeName || `Técnico ${closure.employeeId}`}</span>
                        <span className="text-xs text-gray-400">· {fmtDateTime(closure.disputeReplyAt)}</span>
                      </div>
                      <p className="text-sm text-gray-800 whitespace-pre-wrap">{closure.disputeReply}</p>
                    </div>
                  ) : dispute === 'open' ? (
                    <p className="text-xs text-gray-400 italic text-center py-2">Esperando respuesta del técnico...</p>
                  ) : null}

                  {/* Cierre */}
                  {dispute === 'closed' && closure.disputeClosedAt && (
                    <div className="text-center text-xs text-gray-400">
                      ✓ Disputa cerrada el {fmtDateTime(closure.disputeClosedAt)}
                    </div>
                  )}

                  {/* Botón cerrar disputa */}
                  {dispute === 'open' && (
                    <div className="flex justify-end pt-1">
                      <button
                        onClick={handleCloseDispute}
                        disabled={closing}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-40 text-white rounded-lg text-sm font-semibold transition-colors"
                      >
                        {closing ? 'Cerrando...' : '✓ Cerrar disputa'}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {err && <p className="text-xs text-red-600">{err}</p>}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default RouteDetailModal;
